import { DataTable } from "../../Components/ui/table";
import { DataTableColumn } from "../../Components/ui/table/types";

import { DateRange } from "react-day-picker";

import { PageLayout } from "../../Components/PageLayout";

import { usePaginatedQuery } from "../../Hooks/use-paginated-query";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../Auth/AuthProvider";

import { CursorPagination } from "../../Components/ui/table/pagination";
import { FiltersWrapper } from "../../Components/ui/table/filters";
import { InputFilter } from "../../Components/ui/table/filters/input";

import { renderStatus, renderPriorityStatus } from "./utility/render-status";

import { ConvertToLocalTime } from "../../utils/parse-times";

import { Button } from "../../Components/ui/button";
import { SelectFilter } from "../../Components/ui/table/filters/select/select-filter";
import {
  SearchSelectFilter,
  SearchOption,
} from "../../Components/ui/table/filters/search-select.tsx/search-select";

import DatePicker from "../../Components/ui/table/filters/date-picker/date-picker";
import { toDateInputValue } from "../../Components/ui/table/filters/date-picker/shared";
import { NewMaintenanceReport } from "./Modals/NewMaintenanceReport";
import ExportReports from "./Modals/ExportReports";
import {
  buildSearchOption,
  buildSearchOptionFromSearchParams,
  parseDateFromSearchParam,
} from "../../utils/search-params";

type TruckAsset = {
  id: number;
  company: string;
  name: string;
};

type TrailerAsset = {
  id: number;
  unit: string;
};

type Location = {
  id: number;
  loc_name: string;
};

type MaintenanceReport = {
  id: number;
  order_number: string;
  location: Location;
  assigned_spot: string;
  trailer: TrailerAsset | null;
  truck: TruckAsset | null;
  status: string;
  priority: string;
  created_at: string;
  completed_at: string | null;
  expected_date: string | null;
};

const columns: DataTableColumn<MaintenanceReport>[] = [
  {
    header: "Order",
    labelKey: "order",
    field: "order_number",
    sortable: true,
  },
  {
    header: "Trailer",
    labelKey: "trailer",
    accessor: (row: MaintenanceReport) => row.trailer?.unit ?? "-",
  },
  {
    header: "Truck",
    labelKey: "truck",
    accessor: (row: MaintenanceReport) => {
      const truck = row.truck;
      if (truck) return `${truck.company} - ${truck.name}`;
      return "-";
    },
  },
  {
    header: "Location",
    labelKey: "location",
    accessor: (row: MaintenanceReport) => row.location?.loc_name ?? "-",
  },
  {
    header: "Assigned Spot",
    labelKey: "assignedSpot",
    field: "assigned_spot",
    sortable: true,
  },
  {
    header: "Priority",
    labelKey: "priority",
    cell: (row: MaintenanceReport) => {
      return renderPriorityStatus(row.priority);
    },
    sortable: true,
  },
  {
    header: "Status",
    labelKey: "status",
    cell: (row: MaintenanceReport) => {
      return renderStatus(row.status);
    },
  },
  {
    header: "Estimated Completion",
    labelKey: "expectedDate",
    accessor: (row: MaintenanceReport) =>
      row.expected_date ? ConvertToLocalTime(row.expected_date) : "-",
    sortable: true,
  },
  {
    header: "Created At",
    labelKey: "createdAt",
    accessor: (row: MaintenanceReport) => ConvertToLocalTime(row.created_at),
    sortable: true,
  },
  {
    header: "Completed At",
    labelKey: "completedAt",
    accessor: (row: MaintenanceReport) =>
      row.completed_at ? ConvertToLocalTime(row.completed_at) : "-",
    sortable: true,
  },
];

const initialUrl = "/safety/maintenance-report/";

const MaintenanceReportsRoute = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialStartDate = parseDateFromSearchParam(searchParams.get("start_date"));
  const initialEndDate = parseDateFromSearchParam(searchParams.get("end_date"));
  const initialVehicleType = searchParams.get("vehicle_type");
  const initialVehicleId = searchParams.get("vehicle_id");
  const initialVehicleLabel =
    searchParams.get("vehicle_label") ?? initialVehicleId ?? "";
  const initialVehicleOption =
    initialVehicleType
      ? buildSearchOption(initialVehicleId, initialVehicleLabel)
      : null;

  const [dateRange, setDateRange] = useState<DateRange | undefined>(
    initialStartDate || initialEndDate
      ? {
          from: initialStartDate,
          to: initialEndDate,
        }
      : undefined,
  );
  const [statusFilter, setStatusFilter] = useState<string>(
    searchParams.get("status") ?? "",
  );
  const [orderNumberFilter, setOrderNumberFilter] = useState<string>(
    searchParams.get("order_number") ?? "",
  );
  const [locationFilter, setLocationFilter] = useState<SearchOption | null>(
    buildSearchOptionFromSearchParams(
      searchParams,
      "location",
      "location_label",
    ),
  );
  const [vehicleType, setVehicleType] = useState<"trailer" | "truck" | null>(
    initialVehicleType === "trailer" || initialVehicleType === "truck"
      ? initialVehicleType
      : null,
  );
  const [truckFilter, setTruckFilter] = useState<SearchOption | null>(
    initialVehicleType === "truck" ? initialVehicleOption : null,
  );
  const [trailerFilter, setTrailerFilter] = useState<SearchOption | null>(
    initialVehicleType === "trailer" ? initialVehicleOption : null,
  );
  const [carrierFilter, setCarrierFilter] = useState<string>(
    searchParams.get("carrier") ?? "",
  );
  const [orderBy, setOrderBy] = useState<string>(
    searchParams.get("order_by") ?? "status",
  );
  const [exportModal, setExportModal] = useState<boolean>(false);

  const [createReport, setCreateReport] = useState<boolean>(false);

  const statusOptions = useMemo(
    () => [
      {
        optionLabel: "Status",
        optionValue: "",
      },
      {
        optionLabel: t("searchFilters.maintenanceReportStatus.open"),
        optionValue: "open",
      },
      {
        optionLabel: t("searchFilters.maintenanceReportStatus.inShop"),
        optionValue: "in-shop",
      },
      {
        optionLabel: t("searchFilters.maintenanceReportStatus.inspection"),
        optionValue: "inspection",
      },
      {
        optionLabel: t("searchFilters.maintenanceReportStatus.inProgress"),
        optionValue: "in-progress",
      },
      {
        optionLabel: t("searchFilters.maintenanceReportStatus.waitingParts"),
        optionValue: "waiting-parts",
      },
      {
        optionLabel: t("searchFilters.maintenanceReportStatus.tasksCompleted"),
        optionValue: "tasks-completed",
      },
      {
        optionLabel: t("searchFilters.maintenanceReportStatus.completed"),
        optionValue: "completed",
      },
      {
        optionLabel: t("searchFilters.maintenanceReportStatus.closed"),
        optionValue: "closed",
      },
      {
        optionLabel: t("searchFilters.maintenanceReportStatus.pending"),
        optionValue: "pending",
      },
    ],
    [t],
  );

  const orderByOptions = useMemo(
    () => [
      {
        optionLabel: t("searchFilters.maintenanceReportOrderBy.status"),
        optionValue: "status",
      },
      {
        optionLabel: t("searchFilters.maintenanceReportOrderBy.orderNumber"),
        optionValue: "order_number",
      },
    ],
    [t],
  );

  const handleVehicleOption = (
    option: SearchOption | null,
    vehicleType: "trailer" | "truck",
  ) => {
    if (vehicleType === "trailer") {
      if (option === null) {
        setTrailerFilter(null);
        setVehicleType(null);
        return;
      }
      setTruckFilter(null);
      setTrailerFilter(option);
      setVehicleType("trailer");
    } else if (vehicleType === "truck") {
      if (option === null) {
        setTruckFilter(null);
        setVehicleType(null);
        return;
      }
      setTrailerFilter(null);
      setTruckFilter(option);
      setVehicleType("truck");
    }
  };

  const params = useMemo(
    () => {
      const vehicleOption =
        vehicleType === "trailer" ? trailerFilter : vehicleType === "truck" ? truckFilter : null;

      return {
      ...(orderNumberFilter.trim()
        ? { order_number: orderNumberFilter.trim() }
        : {}),
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(toDateInputValue(dateRange?.from)
        ? { start_date: toDateInputValue(dateRange?.from) }
        : {}),
      ...(toDateInputValue(dateRange?.to)
        ? { end_date: toDateInputValue(dateRange?.to) }
        : {}),
      ...(vehicleType && vehicleOption
        ? {
            vehicle_type: vehicleType,
            vehicle_id: vehicleOption.value,
          }
        : {}),
      ...(locationFilter ? { location: locationFilter.value } : {}),
      ...(carrierFilter ? { carrier: carrierFilter } : {}),
      order_by: orderBy,
    };
    },
    [
      dateRange,
      statusFilter,
      orderNumberFilter,
      locationFilter,
      orderBy,
      trailerFilter,
      truckFilter,
      vehicleType,
      carrierFilter,
    ],
  );

  useEffect(() => {
    const nextSearchParams = new URLSearchParams();
    const startDate = toDateInputValue(dateRange?.from);
    const endDate = toDateInputValue(dateRange?.to);

    if (statusFilter) nextSearchParams.set("status", statusFilter);
    if (orderNumberFilter.trim()) {
      nextSearchParams.set("order_number", orderNumberFilter.trim());
    }
    if (startDate) nextSearchParams.set("start_date", startDate);
    if (endDate) nextSearchParams.set("end_date", endDate);

    if (locationFilter) {
      nextSearchParams.set("location", String(locationFilter.value));
      nextSearchParams.set("location_label", locationFilter.label);
    }

    const vehicleOption =
      vehicleType === "trailer"
        ? trailerFilter
        : vehicleType === "truck"
          ? truckFilter
          : null;

    if (vehicleType && vehicleOption) {
      nextSearchParams.set("vehicle_type", vehicleType);
      nextSearchParams.set("vehicle_id", String(vehicleOption.value));
      nextSearchParams.set("vehicle_label", vehicleOption.label);
    }

    if (carrierFilter) nextSearchParams.set("carrier", carrierFilter);
    if (orderBy) nextSearchParams.set("order_by", orderBy);

    if (nextSearchParams.toString() !== searchParams.toString()) {
      setSearchParams(nextSearchParams, { replace: true });
    }
  }, [
    carrierFilter,
    dateRange,
    locationFilter,
    orderBy,
    orderNumberFilter,
    searchParams,
    setSearchParams,
    statusFilter,
    trailerFilter,
    truckFilter,
    vehicleType,
  ]);

  const results = usePaginatedQuery<MaintenanceReport>({
    initialUrl: initialUrl,
    params,
  });

  const trailerSearchUrl = "/assets/out-trailers?q=";

  const isAuthorized = user.groups.find((group: any) =>
    ["Admin", "Safety", "Maintenance"].includes(group.name),
  );

  return (
    <PageLayout title="Maintenance" header="repairs">
      {exportModal && <ExportReports close={() => setExportModal(false)} />}
      {createReport && (
        <NewMaintenanceReport close={() => setCreateReport(false)} />
      )}
      <FiltersWrapper>
        <DatePicker value={dateRange} onChange={setDateRange} />
        <InputFilter
          value={orderNumberFilter}
          onChange={setOrderNumberFilter}
          placeholder="order"
        />
        <SelectFilter
          value={statusFilter}
          onChange={setStatusFilter}
          options={statusOptions}
        />
        <SelectFilter
          value={orderBy}
          onChange={setOrderBy}
          options={orderByOptions}
        />
        <SearchSelectFilter
          value={trailerFilter}
          onChange={(option) => handleVehicleOption(option, "trailer")}
          url={trailerSearchUrl}
          accessor={{
            optionLabel: (item) => item?.unit,
            optionValue: (item) => item?.id,
          }}
          placeholder={t("common:fields.trailer")}
        />
        <SearchSelectFilter
          value={locationFilter}
          onChange={(option) => setLocationFilter(option)}
          url="/assets/locations?name="
          accessor={{
            optionLabel: (item) => item?.loc_name,
            optionValue: (item) => item?.id,
          }}
          placeholder={t("common:fields.location")}
        />
        <SelectFilter
          value={carrierFilter}
          onChange={setCarrierFilter}
          options={[
            {
              optionLabel: t("common:fields.carrier"),
              optionValue: "",
            },
            {
              optionLabel: "JFG",
              optionValue: "jfg",
            },
            {
              optionLabel: "ATI",
              optionValue: "ati",
            },
            {
              optionLabel: "MFS",
              optionValue: "mfs",
            },
          ]}
        />
        <SearchSelectFilter
          cacheOptions={false}
          url={`assets/get-trucks-out?carrier=${carrierFilter ?? ""}&q=`}
          accessor={{
            optionLabel: (item) => item?.name,
            optionValue: (item) => item?.id,
          }}
          placeholder={t("common:fields.truck")}
          value={truckFilter}
          onChange={(option) => handleVehicleOption(option, "truck")}
        />
        {isAuthorized && (
          <>
            <Button
              variant="positive"
              onClick={() => setCreateReport(true)}
              labelKey="create"
            />
            <Button
              variant="secondary"
              onClick={() => setExportModal(true)}
              labelKey="export"
            />
          </>
        )}
      </FiltersWrapper>
      <div className="margin-bottom-24"></div>
      <CursorPagination
        hasNext={results.hasNext}
        hasPrevious={results.hasPrevious}
        currentPage={results.currentPage}
        fetchNextPage={results.fetchNextPage}
        fetchPreviousPage={results.fetchPreviousPage}
      />
      <DataTable
        columns={columns}
        isLoading={results.isLoading}
        data={results.results ?? []}
        onRowClick={(row: MaintenanceReport) => {
          if (isAuthorized) navigate(`${row.order_number}`);
          else return;
        }}
        getRowKey={(row) => row.id.toString()}
      />
    </PageLayout>
  );
};

export default MaintenanceReportsRoute;
